import moment from "moment";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { isTargetGoalVisibleOnDay } from "../../../../utils/client-goals";
import {
  getHabitsForDay,
  hasTrackingForDay,
} from "../../../../utils/daily-accountability";
import { getNutritionTargetsForPlanDay } from "../../../../utils/nutrition-helpers";
import PlanAnnualEventCard from "./plan-cards/PlanAnnualEventCard";
import PlanAnnualPhaseCard from "./plan-cards/PlanAnnualPhaseCard";
import PlanCheckInCard from "./plan-cards/PlanCheckInCard";
import PlanHabitsCard from "./plan-cards/PlanHabitsCard";
import PlanMealPlansCard from "./plan-cards/PlanMealPlansCard";
import PlanNutritionCard from "./plan-cards/PlanNutritionCard";
import PlanPeriodCard from "./plan-cards/PlanPeriodCard";
import PlanTargetGoalsCard from "./plan-cards/PlanTargetGoalsCard";
import PlanTrackingCard from "./plan-cards/PlanTrackingCard";
import PlanTrainingCard from "./plan-cards/PlanTrainingCard";

// Extracted so PlanView.js's sticky-header clone (useScrollspyStickyHeader)
// can render the exact same date-label markup instead of duplicating it by
// hand. Same naming convention as Calendar Log's own LogDayHeaderLabel
// (LogWeekRow.js) — kept as its own component rather than sharing that one,
// since Plan's real markup genuinely differs (day-name before day-number,
// plus the TODAY text badge Log doesn't render).
export const PlanDayHeaderLabel = ({ day, isToday }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <>
      <span className="day-name">{day.format("ddd")}</span>
      <span className="day-number">{day.format("D")}</span>
      {isToday && <span className="today-badge">{t("calendar.todayBadge")}</span>}
    </>
  );
};

const PlanWeekRow = forwardRef(function PlanWeekRow({
  weekDays,
  collapsed,
  activeFilters,
  nutritionPlans,
  mealPlans,
  habits,
  annualPhases,
  annualEvents,
  targetGoals,
  units = "metric",
  femaleCycleDays,
  trackingItems,
  checkInDays,
  cycles = [],
}, rowRef) {
  const show = (key) => !activeFilters || activeFilters.includes(key);

  const slotDefs = [
    {
      key: "annualPhases",
      // Mirrors PlanAnnualPhaseCard phase matching
      hasContent: () =>
        (annualPhases?.length ?? 0) > 0 &&
        weekDays.some((d) => {
          const u = parseInt(
            moment(d.dayKey, "YYYY-MM-DD").format("YYYYMMDD"),
            10
          );
          return annualPhases.some(
            (p) => p.startDate <= u && (!p.endDate || p.endDate >= u)
          );
        }),
      render: (d) => (
        <PlanAnnualPhaseCard annualPhases={annualPhases} dayKey={d.dayKey} />
      ),
    },
    {
      key: "checkIn",
      hasContent: () => weekDays.some((d) => checkInDays?.includes(d.dayKey)),
      render: (d) => (
        <PlanCheckInCard dayKey={d.dayKey} checkInDays={checkInDays} />
      ),
    },
    {
      key: "training",
      hasContent: () => true,
      render: (d) => (
        <PlanTrainingCard exercises={d.exercises} cycles={cycles} />
      ),
    },
    {
      key: "nutrition",
      hasContent: () =>
        (nutritionPlans?.length ?? 0) > 0 &&
        weekDays.some((d) =>
          getNutritionTargetsForPlanDay(nutritionPlans, d.dayKey, d.exercises)
        ),
      render: (d) => (
        <PlanNutritionCard
          nutritionPlans={nutritionPlans}
          dayKey={d.dayKey}
          exercises={d.exercises}
        />
      ),
    },
    {
      key: "mealPlans",
      hasContent: () =>
        (mealPlans?.length ?? 0) > 0 &&
        weekDays.some((d) => {
          const u = parseInt(
            moment(d.dayKey, "YYYY-MM-DD").format("YYYYMMDD"),
            10
          );
          return mealPlans.some(
            (mp) => mp.utcStartDate <= u && mp.utcEndDate >= u
          );
        }),
      render: (d) => (
        <PlanMealPlansCard mealPlans={mealPlans} dayKey={d.dayKey} />
      ),
    },
    {
      key: "habits",
      hasContent: () =>
        weekDays.some((d) => getHabitsForDay(habits, d.dayKey).length > 0),
      render: (d) => <PlanHabitsCard habits={habits} dayKey={d.dayKey} />,
    },
    {
      key: "tracking",
      hasContent: () =>
        weekDays.some((d) =>
          hasTrackingForDay(trackingItems, d.dayKey, {
            hasTrainingOnDay: (d.exercises?.length ?? 0) > 0,
          })
        ),
      render: (d) => (
        <PlanTrackingCard
          trackingItems={trackingItems}
          dayKey={d.dayKey}
          hasTrainingOnDay={(d.exercises?.length ?? 0) > 0}
        />
      ),
    },
    {
      key: "events",
      hasContent: () =>
        (annualEvents?.length ?? 0) > 0 &&
        weekDays.some((d) => {
          const u = parseInt(
            moment(d.dayKey, "YYYY-MM-DD").format("YYYYMMDD"),
            10
          );
          return annualEvents.some(
            (e) =>
              e.fromDate <= u && (e.toDate ? e.toDate >= u : e.fromDate === u)
          );
        }),
      render: (d) => (
        <PlanAnnualEventCard annualEvents={annualEvents} dayKey={d.dayKey} />
      ),
    },
    {
      key: "goals",
      hasContent: () =>
        (targetGoals?.length ?? 0) > 0 &&
        weekDays.some((d) => {
          const u = parseInt(
            moment(d.dayKey, "YYYY-MM-DD").format("YYYYMMDD"),
            10
          );
          return targetGoals.some((g) => isTargetGoalVisibleOnDay(g, u));
        }),
      render: (d) => (
        <PlanTargetGoalsCard
          targetGoals={targetGoals}
          dayKey={d.dayKey}
          units={units}
        />
      ),
    },
    {
      key: "period",
      hasContent: () =>
        (femaleCycleDays?.length ?? 0) > 0 &&
        weekDays.some((d) => femaleCycleDays.some((c) => c.date === d.dayKey)),
      render: (d) => (
        <PlanPeriodCard femaleCycleDays={femaleCycleDays} dayKey={d.dayKey} />
      ),
    },
  ];

  const activeSlots = slotDefs.filter((s) => show(s.key) && s.hasContent());

  const columnClass = (d) => {
    const isCheckIn = (checkInDays || []).includes(d.dayKey);
    return [
      "plan-day-column",
      !d.isCurrentMonth ? "other-month" : "",
      d.isToday ? "is-today" : "",
      isCheckIn ? "is-check-in" : "",
    ]
      .filter(Boolean)
      .join(" ");
  };

  return (
    <div
      ref={rowRef}
      className={`plan-week-row${collapsed ? " plan-week-row--collapsed" : ""}`}
      style={
        !collapsed
          ? { gridTemplateRows: `repeat(${activeSlots.length + 1}, auto)` }
          : undefined
      }
    >
      {weekDays.map((d) => (
        <div key={d.dayKey} className={columnClass(d)}>
          <div className="day-header">
            <PlanDayHeaderLabel day={d.day} isToday={d.isToday} />
          </div>
          {activeSlots.map((slot) => (
            <div key={slot.key} className="day-slot">
              {slot.render(d)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
});

export default PlanWeekRow;
